import React, { useMemo } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useApiQuery } from "@/lib/useApiQuery";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Bell, Plus, Trash2, Check, Power } from "lucide-react";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { useAuth } from "@/context/AuthContext";
import { useProject } from "@/context/ProjectContext";
import { RuleForm } from "@/components/RuleForm";


function statusStyles(status) {
  const map = {
    active: "border-red-200 bg-red-50 text-red-700",
    acknowledged: "border-amber-200 bg-amber-50 text-amber-700",
    resolved: "border-[#B9D6CE] bg-[#F3F8F7] text-[#156F5B]",
  };
  return map[status] || "border-[#E5E7EB] text-[#6B7280]";
}

export default function AlertsPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const { project } = useProject();
  const queryClient = useQueryClient();
  const params = useMemo(() => (project ? { project } : {}), [project]);
  const { data: alertData, loading } = useApiQuery(["alerts", params], "/alerts", { params });
  const { data: ruleData } = useApiQuery(["alertRules", params], "/alerts/rules", { params });
  const alerts = alertData || [];
  const rules = ruleData || [];

  const load = () => {
    queryClient.invalidateQueries({ queryKey: ["alerts"] });
    queryClient.invalidateQueries({ queryKey: ["alertRules"] });
  };

  const ack = async (id) => {
    try {
      await api.post(`/alerts/${id}/ack`);
      toast.success("Alert acknowledged");
      load();
    } catch (e) {
      toast.error("Failed to acknowledge", { description: formatApiErrorDetail(e?.response?.data?.detail) || e.message });
    }
  };

  const toggleRule = async (rule) => {
    try {
      await api.patch(`/alerts/rules/${rule.id}`, { enabled: !rule.enabled });
      load();
    } catch (e) {
      toast.error("Failed to update rule", { description: formatApiErrorDetail(e?.response?.data?.detail) || e.message });
    }
  };

  const deleteRule = async (rule) => {
    try {
      await api.delete(`/alerts/rules/${rule.id}`);
      toast.success("Rule deleted");
      load();
    } catch (e) {
      toast.error("Failed to delete rule", { description: formatApiErrorDetail(e?.response?.data?.detail) || e.message });
    }
  };

  const open = alerts.filter((a) => a.status !== "resolved");
  const resolved = alerts.filter((a) => a.status === "resolved");

  return (
    <div className="max-w-7xl fade-up">
      <div className="k-label flex items-center gap-2">
        <Bell className="h-3.5 w-3.5" /> Monitoring
      </div>
      <h1 className="mt-2 text-4xl sm:text-5xl font-bold font-display text-[#212121]">Alerts</h1>
      <p className="mt-3 text-[#4D4D4D] text-sm max-w-2xl">
        Threshold rules are evaluated on every data sync. Breaches fire alerts instantly.
      </p>

      {isAdmin && (
        <div className="mt-8">
          <RuleForm onCreated={load} />
        </div>
      )}

      {/* Active alerts */}
      <div className="mt-10">
        <h2 className="text-xl font-heading font-bold text-[#212121]">
          Active <span className="text-red-600 text-base align-middle">({open.length})</span>
        </h2>
        {loading ? (
          <div className="mt-4 k-sublabel">Loading…</div>
        ) : open.length === 0 ? (
          <div className="mt-4 k-card p-6 text-[#6B7280] text-sm" data-testid="no-active-alerts">
            All clear. No active alerts.
          </div>
        ) : (
          <div className="mt-4 space-y-3" data-testid="active-alerts-list">
            {open.map((a, i) => (
              <div key={a.id} className={`rounded-md border p-5 flex items-start justify-between gap-4 flex-wrap ${statusStyles(a.status)}`} data-testid={`alert-card-${i}`}>
                <div>
                  <div className="flex items-center gap-3 flex-wrap">
                    <span className="text-[10px] font-heading font-bold uppercase tracking-wider border border-current rounded px-2 py-0.5">{a.status}</span>
                    <span className="text-[#212121] font-heading font-semibold">{a.label || a.point_dis}</span>
                    <span className="text-xs text-[#6B7280]">{a.site_dis}</span>
                  </div>
                  <div className="mt-2 text-sm">{a.message}</div>
                  <div className="mt-1 text-[11px] text-[#9CA3AF]">
                    Triggered {new Date(a.triggered_at).toLocaleString()}
                    {a.acknowledged_by ? ` • ack by ${a.acknowledged_by}` : ""}
                  </div>
                </div>
                {a.status === "active" && (
                  <button onClick={() => ack(a.id)} data-testid={`alert-ack-btn-${i}`} className="k-btn-outline">
                    <Check className="h-3 w-3" /> Acknowledge
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Rules */}
      <div className="mt-12">
        <h2 className="text-xl font-heading font-bold text-[#212121]">Rules</h2>
        {rules.length === 0 ? (
          <div className="mt-4 k-card p-6 text-[#6B7280] text-sm" data-testid="no-rules">
            No rules yet.{isAdmin ? " Create one above to start monitoring." : " Ask an admin to create thresholds."}
          </div>
        ) : (
          <div className="mt-4 k-card overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow className="border-[#E5E7EB] hover:bg-transparent bg-[#F9FAFB]">
                  <TableHead className="k-sublabel">Rule</TableHead>
                  <TableHead className="k-sublabel">Site</TableHead>
                  <TableHead className="k-sublabel">Condition</TableHead>
                  <TableHead className="k-sublabel">Status</TableHead>
                  {isAdmin && <TableHead className="k-sublabel text-right">Actions</TableHead>}
                </TableRow>
              </TableHeader>
              <TableBody data-testid="rules-table">
                {rules.map((r, i) => (
                  <TableRow key={r.id} className="border-[#F0F2F1] hover:bg-[#F9FAFB]" data-testid={`rule-row-${i}`}>
                    <TableCell className="text-sm">
                      <div className="text-[#212121] font-heading font-semibold">{r.label || r.point_dis}</div>
                      <div className="text-[11px] text-[#9CA3AF]">{r.point_dis}</div>
                    </TableCell>
                    <TableCell className="text-xs text-[#6B7280]">{r.site_dis || "—"}</TableCell>
                    <TableCell className="text-sm font-heading font-semibold text-[#156F5B]">
                      {r.operator} {r.threshold}{r.unit ? ` ${r.unit}` : ""}
                    </TableCell>
                    <TableCell>
                      <span className={`text-[10px] font-heading font-bold uppercase tracking-wider rounded px-2 py-0.5 border ${r.enabled ? "border-[#B9D6CE] bg-[#F3F8F7] text-[#156F5B]" : "border-[#E5E7EB] text-[#9CA3AF]"}`}>
                        {r.enabled ? "enabled" : "paused"}
                      </span>
                    </TableCell>
                    {isAdmin && (
                      <TableCell className="text-right">
                        <div className="inline-flex gap-2">
                          <button
                            onClick={() => toggleRule(r)}
                            title={r.enabled ? "Pause rule" : "Enable rule"}
                            data-testid={`rule-toggle-btn-${i}`}
                            className="p-1.5 rounded-md border border-[#E5E7EB] text-[#6B7280] hover:border-[#156F5B] hover:text-[#156F5B]"
                            style={{ transition: "color .2s ease, border-color .2s ease" }}
                          >
                            <Power className="h-3.5 w-3.5" />
                          </button>
                          <button
                            onClick={() => deleteRule(r)}
                            title="Delete rule"
                            data-testid={`rule-delete-btn-${i}`}
                            className="p-1.5 rounded-md border border-[#E5E7EB] text-[#6B7280] hover:border-red-300 hover:text-red-600"
                            style={{ transition: "color .2s ease, border-color .2s ease" }}
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                        </div>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </div>

      {/* Resolved history */}
      <div className="mt-12">
        <h2 className="text-xl font-heading font-bold text-[#212121]">Resolved history</h2>
        {resolved.length === 0 ? (
          <div className="mt-4 text-[#6B7280] text-sm" data-testid="no-resolved-alerts">No resolved alerts yet.</div>
        ) : (
          <div className="mt-4 k-card overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow className="border-[#E5E7EB] hover:bg-transparent bg-[#F9FAFB]">
                  <TableHead className="k-sublabel">Alert</TableHead>
                  <TableHead className="k-sublabel">Site</TableHead>
                  <TableHead className="k-sublabel">Triggered</TableHead>
                  <TableHead className="k-sublabel">Resolved</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody data-testid="resolved-alerts-table">
                {resolved.slice(0, 50).map((a, i) => (
                  <TableRow key={a.id} className="border-[#F0F2F1] hover:bg-[#F9FAFB]" data-testid={`resolved-row-${i}`}>
                    <TableCell className="text-sm text-[#212121]">{a.message}</TableCell>
                    <TableCell className="text-xs text-[#6B7280]">{a.site_dis || "—"}</TableCell>
                    <TableCell className="text-xs text-[#6B7280]">{new Date(a.triggered_at).toLocaleString()}</TableCell>
                    <TableCell className="text-xs font-semibold text-[#156F5B]">{a.resolved_at ? new Date(a.resolved_at).toLocaleString() : "—"}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </div>
    </div>
  );
}
